import { clsx } from "clsx";
import { useEffect, useRef, useState } from "react";
import type { ButtonHTMLAttributes } from "react";

type Variant = "primary" | "secondary" | "ghost" | "danger";

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: "sm" | "md";
  // Opt-in two-click confirmation for destructive / irreversible
  // actions. First click flips the button into a red "<confirm>"
  // state; the second click inside 4 seconds (or until the button
  // loses focus) actually fires onClick. Pass the label to show
  // while armed, e.g. confirm="Confirm delete". Set confirm=true to
  // use a generic "Confirm?" label. Omit for a normal button.
  confirm?: string | boolean;
}

const styles: Record<Variant, string> = {
  primary: "bg-brand-600 hover:bg-brand-700 text-white",
  secondary: "bg-white border border-slate-300 hover:bg-slate-50 text-slate-900",
  ghost: "hover:bg-slate-100 text-slate-700",
  danger: "bg-red-600 hover:bg-red-700 text-white",
};

const CONFIRM_RESET_MS = 4000;

export function Button({
  variant = "primary",
  size = "md",
  className,
  confirm,
  children,
  onClick,
  title,
  ...rest
}: Props) {
  const [armed, setArmed] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Nothing to confirm → render a bog-standard button so the extra
  // state machinery has no footprint on non-destructive callers.
  if (!confirm) {
    return (
      <button
        {...rest}
        onClick={onClick}
        title={title}
        className={clsx(
          "inline-flex items-center justify-center font-medium rounded-md transition",
          "disabled:opacity-50 disabled:pointer-events-none",
          size === "sm" ? "px-2.5 py-1 text-sm" : "px-3.5 py-2 text-sm",
          styles[variant],
          className,
        )}
      >
        {children}
      </button>
    );
  }

  const armedLabel = typeof confirm === "string" ? confirm : "Confirm?";

  return (
    <button
      {...rest}
      onClick={(e) => {
        if (armed) {
          setArmed(false);
          if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
          }
          onClick?.(e);
        } else {
          e.preventDefault();
          e.stopPropagation();
          setArmed(true);
          if (timerRef.current) clearTimeout(timerRef.current);
          timerRef.current = setTimeout(() => setArmed(false), CONFIRM_RESET_MS);
        }
      }}
      onBlur={(e) => {
        setArmed(false);
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        rest.onBlur?.(e);
      }}
      title={armed ? "Click again to confirm" : title}
      className={clsx(
        "inline-flex items-center justify-center font-medium rounded-md transition",
        "disabled:opacity-50 disabled:pointer-events-none",
        size === "sm" ? "px-2.5 py-1 text-sm" : "px-3.5 py-2 text-sm",
        armed ? styles.danger : styles[variant],
        className,
      )}
    >
      {armed ? armedLabel : children}
    </button>
  );
}
